import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import emendaService, { resolveEmendaStatus } from '../services/emendaService';
import secretariaMunicipalService, { type SecretariaMunicipalDTO } from '../services/secretariaMunicipalService';
import ConfirmActionModal from './ConfirmActionModal';
import ActionButton from './ui/ActionButton';
import PanelHeader from './ui/PanelHeader';
import StatusBadge, { type BadgeTone } from './ui/StatusBadge';
import Card from './ui/Card';
import { FIELD_CLASS } from './ui/fieldStyles';
import { toUpperText } from '../utils/formatters';

interface AdmissibilidadeDTO {
  id: number;
  emendaId: string;
  status: 'APROVADA' | 'REPROVADA' | 'PENDENTE';
  observacao?: string;
  analistaNome?: string;
  dataAnalise?: string;
}

interface Props {
  emendaId: string;
  emendaStatus?: string;
  canAct?: boolean;
  onStatusChange?: (newStatus: string) => void;
}


const AdmissibilidadePanel: React.FC<Props> = ({ emendaId, emendaStatus, canAct = false, onStatusChange }) => {
  const queryClient = useQueryClient();
  const [parecer, setParecer] = useState('');
  const [secretaria, setSecretaria] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<{ acao: string; observacao?: string; secretariaDestino?: string } | null>(null);
  const [currentStatus, setCurrentStatus] = useState(emendaStatus ?? 'Recebido');

  useEffect(() => { setCurrentStatus(emendaStatus ?? 'Recebido'); }, [emendaStatus]);

  const { data: admissibilidade, isLoading } = useQuery<AdmissibilidadeDTO | null>({
    queryKey: ['admissibilidade', emendaId],
    queryFn: async () => {
      const res = await api.get(`/emendas/${emendaId}/admissibilidade`);
      if (res.status === 204) return null;
      return res.data;
    },
    retry: false,
  });

  const { data: secretarias = [], isLoading: loadingSecretarias } = useQuery<SecretariaMunicipalDTO[]>({
    queryKey: ['secretarias-municipais', emendaId],
    queryFn: () => secretariaMunicipalService.listByEmenda(emendaId),
    staleTime: 5 * 60 * 1000,
  });

  const secretariasAtivas = secretarias.filter((s) => s.ativo !== false);

  const acaoMutation = useMutation({
    mutationFn: (payload: { acao: string; observacao?: string; secretariaDestino?: string }) =>
      emendaService.executarAcao(emendaId, { acao: payload.acao as any, observacao: payload.observacao, secretariaDestino: payload.secretariaDestino }),
    onSuccess: (updated) => {
      setError(null); setParecer(''); setSecretaria('');
      const nextStatus = resolveEmendaStatus(updated);
      setCurrentStatus(nextStatus);
      queryClient.invalidateQueries({ queryKey: ['admissibilidade', emendaId] });
      queryClient.invalidateQueries({ queryKey: ['historico', emendaId] });
      if (onStatusChange) onStatusChange(nextStatus);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.response?.data?.error ?? 'Erro ao executar a ação'),
  });

  const isRecebido     = !currentStatus || currentStatus === 'Recebido';
  const isEmAnalise    = currentStatus === 'Em análise de admissibilidade';
  const isAprovada     = currentStatus === 'Admissibilidade aprovada';
  const isDevolvida    = currentStatus === 'Devolvida ao legislativo';
  const isFasePosterior = ['Em análise de demanda','Análise de demanda aprovada','Devolvida por incompatibilidade de demanda','Em análise documental','Análise documental aprovada','Devolvida por inviabilidade documental'].includes(currentStatus ?? '');
  const jaFinalizado   = isAprovada || isDevolvida || isFasePosterior;

  const statusLabel = (isAprovada || isFasePosterior) ? 'Aprovada' : isDevolvida ? 'Devolvida' : isEmAnalise ? 'Em análise' : null;
  const statusTone: BadgeTone = isDevolvida ? 'danger' : (isAprovada || isFasePosterior) ? 'success' : 'warning';

  return (
    <Card>
      <PanelHeader title="Admissibilidade" owner="Orçamento"
        right={statusLabel && <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>} />

      <div className="space-y-3 px-4 py-3">
        {/* Parecer registrado */}
        {!isLoading && admissibilidade?.observacao && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-700/40">
            <p className="text-[0.65rem] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Parecer</p>
            <p className="mt-1 text-xs leading-5 text-slate-700 dark:text-slate-200">{admissibilidade.observacao}</p>
            {admissibilidade.analistaNome && (
              <p className="mt-1 text-[0.65rem] text-slate-500 dark:text-slate-400">
                {admissibilidade.analistaNome}{admissibilidade.dataAnalise && ` · ${new Date(admissibilidade.dataAnalise).toLocaleDateString('pt-BR')}`}
              </p>
            )}
          </div>
        )}

        {!canAct && (
          <p className="text-xs text-slate-500 dark:text-slate-400">Somente o setor de Orçamento pode executar esta análise.</p>
        )}

        {canAct && isRecebido && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-slate-100/70 px-4 py-6 text-center dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-sm text-slate-600 dark:text-slate-300">Emenda recebida e pronta para a análise de admissibilidade.</p>
            <ActionButton kind="neutral" size="md" onClick={() => acaoMutation.mutate({ acao: 'INICIAR_ANALISE' })}
              disabled={acaoMutation.isPending} loading={acaoMutation.isPending}>
              {acaoMutation.isPending ? 'Iniciando…' : 'Iniciar análise'}
            </ActionButton>
          </div>
        )}

        {canAct && isEmAnalise && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-100/70 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <div>
              <label htmlFor="parecer-orcamento" className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Parecer técnico</label>
              <textarea
                id="parecer-orcamento"
                value={parecer}
                onChange={(e) => setParecer(toUpperText(e.target.value))}
                rows={3}
                placeholder="Descreva o parecer técnico…"
                className={`${FIELD_CLASS} resize-none`}
              />
            </div>
            <div>
              <label htmlFor="secretaria-orcamento" className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">Secretaria de destino</label>
              <select
                id="secretaria-orcamento"
                value={secretaria}
                onChange={(e) => setSecretaria(e.target.value)}
                disabled={loadingSecretarias}
                className={FIELD_CLASS}
              >
                <option value="">{loadingSecretarias ? 'Carregando…' : 'Selecione a secretaria…'}</option>
                {secretariasAtivas.map((s) => <option key={s.id} value={s.nome}>{s.nome}</option>)}
              </select>
            </div>
            <div className="flex justify-center gap-3 pt-1">
              <ActionButton
                kind="approve"
                size="md"
                onClick={() => {
                  if (!secretaria.trim()) { setError('Informe a secretaria de destino.'); return; }
                  if (!parecer.trim()) { setError('Informe o parecer técnico.'); return; }
                  setError(null);
                  setPendingPayload({ acao: 'APROVAR_ADMISSIBILIDADE', observacao: parecer, secretariaDestino: secretaria });
                  setConfirmOpen(true);
                }}
                disabled={acaoMutation.isPending}
                loading={acaoMutation.isPending && pendingPayload?.acao === 'APROVAR_ADMISSIBILIDADE'}
              >
                Aprovar
              </ActionButton>
              <ActionButton
                kind="reject"
                variant="outline"
                size="md"
                onClick={() => {
                  if (!parecer.trim()) { setError('Informe o parecer técnico.'); return; }
                  setError(null);
                  setPendingPayload({ acao: 'REPROVAR_ADMISSIBILIDADE', observacao: parecer });
                  setConfirmOpen(true);
                }}
                disabled={acaoMutation.isPending}
                loading={acaoMutation.isPending && pendingPayload?.acao === 'REPROVAR_ADMISSIBILIDADE'}
              >
                Reprovar
              </ActionButton>
            </div>
          </div>
        )}

        {canAct && jaFinalizado && (
          isDevolvida ? (
            <p className="text-xs font-medium text-red-600 dark:text-red-400">Emenda devolvida ao legislativo. Veja a justificativa no histórico.</p>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">Análise concluída — admissibilidade aprovada.</p>
          )
        )}

        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>

      <ConfirmActionModal
        open={confirmOpen}
        title={pendingPayload?.acao === 'APROVAR_ADMISSIBILIDADE' ? 'Confirmar aprovação' : 'Confirmar reprovação'}
        message={pendingPayload?.acao === 'APROVAR_ADMISSIBILIDADE' ? 'Confirma a aprovação da admissibilidade desta emenda?' : 'Confirma a reprovação da admissibilidade desta emenda?'}
        confirmLabel={pendingPayload?.acao === 'APROVAR_ADMISSIBILIDADE' ? 'Aprovar' : 'Reprovar'}
        cancelLabel="Cancelar"
        onConfirm={() => { if (pendingPayload) acaoMutation.mutate(pendingPayload); setConfirmOpen(false); }}
        onCancel={() => setConfirmOpen(false)}
        loading={acaoMutation.isPending}
      />
    </Card>
  );
};

export default AdmissibilidadePanel;
